import React, { useState, useEffect } from "react";

interface TimeFormat {
    label: string;
    value: string;
    convert: (timestamp: number) => string;
}

const timeFormats: TimeFormat[] = [
    {
        label: "Local Date & Time",
        value: "local",
        convert: (timestamp: number) => new Date(timestamp * 1000).toLocaleString()
    },
    {
        label: "UTC Date & Time",
        value: "utc",
        convert: (timestamp: number) => new Date(timestamp * 1000).toUTCString()
    },
    {
        label: "ISO 8601",
        value: "iso",
        convert: (timestamp: number) => new Date(timestamp * 1000).toISOString()
    },
    {
        label: "Unix Timestamp",
        value: "unix",
        convert: (timestamp: number) => timestamp.toString()
    },
    {
        label: "Relative Time",
        value: "relative",
        convert: (timestamp: number) => {
            const diff = Math.floor(Date.now() / 1000) - timestamp;
            if (diff < 0) return "In the future";
            if (diff < 60) return `${diff} seconds ago`;
            if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
            if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
            if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
            if (diff < 31536000) return `${Math.floor(diff / 2592000)} months ago`;
            return `${Math.floor(diff / 31536000)} years ago`;
        }
    }
];

const defaultResults = timeFormats.map(format => ({
    format: format.label,
    value: "-"
}));

function TimestampConverter() {
    const [inputType, setInputType] = useState<"timestamp" | "date">("timestamp");
    const [timestamp, setTimestamp] = useState("");
    const [dateInput, setDateInput] = useState("");
    const [results, setResults] = useState<{ format: string; value: string }[]>(defaultResults);
    const [error, setError] = useState("");

    // Update current time every second for relative time
    useEffect(() => {
        const timer = setInterval(() => {
            if (results.length > 0 && results.some(r => r.format === "Relative Time" && r.value !== "—")) {
                convertValue();
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [timestamp, dateInput, inputType]); // eslint-disable-line react-hooks/exhaustive-deps

    const getCurrentTimestamp = () => {
        const now = Math.floor(Date.now() / 1000);
        if (inputType === "timestamp") {
            setTimestamp(now.toString());
        } else {
            setDateInput(new Date().toISOString().slice(0, 16));
        }
    };

    const convertValue = () => {
        setError("");

        try {
            let unixTimestamp: number;

            if (inputType === "timestamp") {
                if (!timestamp.trim()) {
                    setResults(defaultResults);
                    return;
                }
                unixTimestamp = parseInt(timestamp);
                if (isNaN(unixTimestamp)) {
                    setError("Please enter a valid timestamp");
                    setResults(defaultResults);
                    return;
                }
            } else {
                if (!dateInput.trim()) {
                    setResults(defaultResults);
                    return;
                }
                unixTimestamp = Math.floor(new Date(dateInput).getTime() / 1000);
                if (isNaN(unixTimestamp)) {
                    setError("Please enter a valid date");
                    setResults(defaultResults);
                    return;
                }
            }

            const newResults = timeFormats.map(format => ({
                format: format.label,
                value: format.convert(unixTimestamp)
            }));

            setResults(newResults);
        } catch (err) {
            setError("Invalid input format");
            setResults(defaultResults);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            convertValue();
        }
    };

    return (
        <div className="tool">
            <h2>Timestamp Converter</h2>
            <div className="content">
                <div className="timestamp-converter">
                    <div className="timestamp-converter__input-section">
                        <div className="timestamp-converter__input-type">
                            <div className="timestamp-converter__type-selector">
                                <button
                                    className={`timestamp-converter__type-button ${inputType === "timestamp" ? 'active' : ''}`}
                                    onClick={() => setInputType("timestamp")}
                                >
                                    Unix Timestamp
                                </button>
                                <button
                                    className={`timestamp-converter__type-button ${inputType === "date" ? 'active' : ''}`}
                                    onClick={() => setInputType("date")}
                                >
                                    Date & Time
                                </button>
                            </div>
                        </div>

                        <div className="timestamp-converter__input-container">
                            {inputType === "timestamp" ? (
                                <input
                                    type="text"
                                    value={timestamp}
                                    onChange={(e) => {
                                        setTimestamp(e.target.value);
                                        if (!e.target.value.trim()) {
                                            setResults(defaultResults);
                                        }
                                    }}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter Unix timestamp (e.g., 1625097600)"
                                    className="timestamp-converter__input"
                                />
                            ) : (
                                <input
                                    type="datetime-local"
                                    value={dateInput}
                                    onChange={(e) => {
                                        setDateInput(e.target.value);
                                        if (!e.target.value.trim()) {
                                            setResults(defaultResults);
                                        }
                                    }}
                                    className="timestamp-converter__input"
                                />
                            )}
                            <button 
                                onClick={getCurrentTimestamp}
                                className="timestamp-converter__current-time"
                            >
                                Current
                            </button>
                        </div>

                        <button 
                            onClick={convertValue}
                            className="timestamp-converter__convert-button"
                        >
                            Convert
                        </button>
                    </div>

                    <div className="timestamp-converter__results">
                        {error && <p className="error-message">{error}</p>}
                        {results.map((result, index) => (
                            <div>
                                <p><strong>{result.format}:</strong> {result.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TimestampConverter;
